// Per-dialect synthesizer for tablespace + filegroup ops (v2 Slice E2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice E2.
//
// Per-engine surface:
//   - pgsql / mysql / oracle: tablespaces (`CREATE/ALTER/DROP TABLESPACE`).
//   - mssql: filegroups + database files via `ALTER DATABASE <db> …`. mssql
//            does not have tablespaces; it has filegroups owned by the
//            database, with files added/modified through ALTER DATABASE.
//   - sqlite: not supported.

import type { SqlEngine } from '../../../sql-grammar/model';
import type {
  DatabaseFileChange,
  DatabaseFileDef,
  FilegroupDef,
  TablespaceChange,
  TablespaceDef,
} from '../../types';
import { DdlEditError } from '../../types';

function unsupported(engine: SqlEngine, op: string, alternative: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${alternative}`,
  );
}

function rejectField(engine: SqlEngine, op: string, field: string, allowed: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} ${op} does not accept '${field}' — only ${allowed} accept this field.`,
  );
}

// ─── create-tablespace ────────────────────────────────────────────────────

export function synthesizeCreateTablespace(
  engine: SqlEngine,
  ts: TablespaceDef,
): string {
  if (engine === 'mssql') {
    unsupported(
      engine,
      'create-tablespace',
      `mssql has no tablespace concept — use op:'create-filegroup' + `
        + `op:'add-database-file' instead.`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'create-tablespace', `sqlite has no storage abstraction.`);
  }
  if (engine === 'postgres') {
    if (ts.mysqlEngine !== undefined) {
      rejectField(engine, 'create-tablespace', 'mysqlEngine', 'mysql');
    }
    if (
      ts.oracleSize !== undefined
      || ts.oracleDatafileSize !== undefined
      || ts.oracleAutoextendText !== undefined
      || ts.oracleLogging !== undefined
      || ts.oracleSegmentSpaceManagement !== undefined
    ) {
      rejectField(engine, 'create-tablespace', 'oracle*', 'oracle');
    }
    let s = `CREATE TABLESPACE ${ts.name}`;
    if (ts.owner !== undefined) s += ` OWNER ${ts.owner}`;
    s += ` LOCATION '${ts.location}'`;
    if (ts.pgsqlWithOptions !== undefined && ts.pgsqlWithOptions.length > 0) {
      s += ` WITH (${ts.pgsqlWithOptions.join(', ')})`;
    }
    return s;
  }
  if (engine === 'mysql') {
    if (ts.owner !== undefined) {
      rejectField(engine, 'create-tablespace', 'owner', 'pgsql');
    }
    if (ts.pgsqlWithOptions !== undefined) {
      rejectField(engine, 'create-tablespace', 'pgsqlWithOptions', 'pgsql');
    }
    if (
      ts.oracleSize !== undefined
      || ts.oracleDatafileSize !== undefined
      || ts.oracleAutoextendText !== undefined
      || ts.oracleLogging !== undefined
      || ts.oracleSegmentSpaceManagement !== undefined
    ) {
      rejectField(engine, 'create-tablespace', 'oracle*', 'oracle');
    }
    let s = `CREATE TABLESPACE ${ts.name} ADD DATAFILE '${ts.location}'`;
    if (ts.mysqlEngine !== undefined) s += ` ENGINE = ${ts.mysqlEngine}`;
    return s;
  }
  // oracle
  if (ts.owner !== undefined) {
    rejectField(engine, 'create-tablespace', 'owner', 'pgsql');
  }
  if (ts.pgsqlWithOptions !== undefined) {
    rejectField(engine, 'create-tablespace', 'pgsqlWithOptions', 'pgsql');
  }
  if (ts.mysqlEngine !== undefined) {
    rejectField(engine, 'create-tablespace', 'mysqlEngine', 'mysql');
  }
  let s = 'CREATE';
  if (ts.oracleSize !== undefined) s += ` ${ts.oracleSize.toUpperCase()}`;
  s += ` TABLESPACE ${ts.name} DATAFILE '${ts.location}'`;
  if (ts.oracleDatafileSize !== undefined) s += ` SIZE ${ts.oracleDatafileSize}`;
  if (ts.oracleAutoextendText !== undefined) s += ` ${ts.oracleAutoextendText}`;
  if (ts.oracleLogging !== undefined) s += ` ${ts.oracleLogging.toUpperCase()}`;
  if (ts.oracleSegmentSpaceManagement !== undefined) {
    s += ` SEGMENT SPACE MANAGEMENT ${ts.oracleSegmentSpaceManagement.toUpperCase()}`;
  }
  return s;
}

// ─── alter-tablespace ─────────────────────────────────────────────────────

export function synthesizeAlterTablespace(
  engine: SqlEngine,
  tablespaceName: string,
  change: TablespaceChange,
): string {
  if (engine === 'mssql') {
    unsupported(
      engine,
      'alter-tablespace',
      `mssql has no tablespace concept — modify filegroup files via `
        + `op:'modify-database-file'.`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'alter-tablespace', `sqlite has no storage abstraction.`);
  }
  const { renameTo, ownerTo, pgsqlSetOptions, mysqlEngine } = change;
  if (
    renameTo === undefined
    && ownerTo === undefined
    && pgsqlSetOptions === undefined
    && mysqlEngine === undefined
  ) {
    throw new Error(
      `applyDdlEdit: alter-tablespace requires at least one change field — `
        + `renameTo / ownerTo / pgsqlSetOptions / mysqlEngine.`,
    );
  }
  if (engine === 'postgres') {
    if (mysqlEngine !== undefined) {
      rejectField(engine, 'alter-tablespace', 'mysqlEngine', 'mysql');
    }
    if (renameTo !== undefined) {
      return `ALTER TABLESPACE ${tablespaceName} RENAME TO ${renameTo}`;
    }
    if (ownerTo !== undefined) {
      return `ALTER TABLESPACE ${tablespaceName} OWNER TO ${ownerTo}`;
    }
    // pgsqlSetOptions
    return `ALTER TABLESPACE ${tablespaceName} SET (${pgsqlSetOptions!.join(', ')})`;
  }
  if (engine === 'mysql') {
    if (ownerTo !== undefined) {
      rejectField(engine, 'alter-tablespace', 'ownerTo', 'pgsql');
    }
    if (pgsqlSetOptions !== undefined) {
      rejectField(engine, 'alter-tablespace', 'pgsqlSetOptions', 'pgsql');
    }
    if (renameTo !== undefined) {
      return `ALTER TABLESPACE ${tablespaceName} RENAME TO ${renameTo}`;
    }
    // mysqlEngine
    return `ALTER TABLESPACE ${tablespaceName} ENGINE = ${mysqlEngine!}`;
  }
  // oracle
  if (ownerTo !== undefined) {
    rejectField(engine, 'alter-tablespace', 'ownerTo', 'pgsql');
  }
  if (pgsqlSetOptions !== undefined) {
    rejectField(engine, 'alter-tablespace', 'pgsqlSetOptions', 'pgsql');
  }
  if (mysqlEngine !== undefined) {
    rejectField(engine, 'alter-tablespace', 'mysqlEngine', 'mysql');
  }
  // renameTo only on oracle
  return `ALTER TABLESPACE ${tablespaceName} RENAME TO ${renameTo!}`;
}

// ─── drop-tablespace ──────────────────────────────────────────────────────

export function synthesizeDropTablespace(
  engine: SqlEngine,
  tablespaceName: string,
  ifExists: boolean,
): string {
  if (engine === 'mssql') {
    unsupported(
      engine,
      'drop-tablespace',
      `mssql has no tablespace concept — use op:'drop-filegroup' instead.`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'drop-tablespace', `sqlite has no storage abstraction.`);
  }
  let s = 'DROP TABLESPACE ';
  if (ifExists) s += 'IF EXISTS ';
  s += tablespaceName;
  return s;
}

// ─── create-filegroup (mssql-only) ────────────────────────────────────────

export function synthesizeCreateFilegroup(
  engine: SqlEngine,
  fg: FilegroupDef,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'create-filegroup',
      `filegroups are an mssql-only construct; other engines have tablespaces `
        + `— use op:'create-tablespace' instead.`,
    );
  }
  return `ALTER DATABASE ${fg.database} ADD FILEGROUP ${fg.name}`;
}

// ─── drop-filegroup (mssql-only) ──────────────────────────────────────────

export function synthesizeDropFilegroup(
  engine: SqlEngine,
  fg: FilegroupDef,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'drop-filegroup',
      `filegroups are an mssql-only construct — use op:'drop-tablespace' instead.`,
    );
  }
  return `ALTER DATABASE ${fg.database} REMOVE FILEGROUP ${fg.name}`;
}

// ─── add-database-file (mssql-only) ───────────────────────────────────────

function fileBodyClauses(
  file: { readonly size?: string; readonly maxsize?: string; readonly filegrowth?: string },
): readonly string[] {
  const parts: string[] = [];
  if (file.size !== undefined) parts.push(`SIZE = ${file.size}`);
  if (file.maxsize !== undefined) parts.push(`MAXSIZE = ${file.maxsize}`);
  if (file.filegrowth !== undefined) parts.push(`FILEGROWTH = ${file.filegrowth}`);
  return parts;
}

export function synthesizeAddDatabaseFile(
  engine: SqlEngine,
  file: DatabaseFileDef,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'add-database-file',
      `database files are an mssql-only construct; other engines manage `
        + `storage through tablespaces — use op:'create-tablespace' instead.`,
    );
  }
  const body = [
    `NAME = ${file.name}`,
    `FILENAME = '${file.filename}'`,
    ...fileBodyClauses(file),
  ];
  let s = `ALTER DATABASE ${file.database} ADD FILE (${body.join(', ')})`;
  if (file.filegroup !== undefined) s += ` TO FILEGROUP ${file.filegroup}`;
  return s;
}

// ─── modify-database-file (mssql-only) ────────────────────────────────────

export function synthesizeModifyDatabaseFile(
  engine: SqlEngine,
  database: string,
  fileName: string,
  change: DatabaseFileChange,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'modify-database-file',
      `database files are an mssql-only construct — use op:'alter-tablespace' instead.`,
    );
  }
  const { newName, size, maxsize, filegrowth } = change;
  if (
    newName === undefined
    && size === undefined
    && maxsize === undefined
    && filegrowth === undefined
  ) {
    throw new Error(
      `applyDdlEdit: modify-database-file requires at least one change field — `
        + `newName / size / maxsize / filegrowth.`,
    );
  }
  const body: string[] = [`NAME = ${fileName}`];
  if (newName !== undefined) body.push(`NEWNAME = ${newName}`);
  body.push(...fileBodyClauses({ size, maxsize, filegrowth }));
  return `ALTER DATABASE ${database} MODIFY FILE (${body.join(', ')})`;
}
